import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import type { PostHog } from 'posthog-js';

import { POSTHOG_CONFIG, POSTHOG_KEY_PLACEHOLDER } from './posthog.config';
import { PosthogEventMap, PosthogEventName } from './posthog-events';

/**
 * Punto único de integración con PostHog para el dashboard.
 *
 * - `posthog-js` se carga con **import dinámico** para que quede en un chunk lazy
 *   y no infle el bundle inicial (mejora el primer render).
 * - Se inicializa una sola vez, en el navegador y solo si hay una key real
 *   configurada (evita mandar eventos desde builds sin credenciales).
 * - El init corre FUERA de la zona de Angular para no disparar ciclos de change
 *   detection (recomendación oficial, importante para el session recording).
 * - La captura de eventos de producto pasa por `captureEvent`, que es TIPADO
 *   contra `PosthogEventMap` (fuente única). No usar `posthog.capture` directo.
 */
@Injectable({ providedIn: 'root' })
export class PosthogService {
  private readonly _ngZone = inject(NgZone);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _config = inject(POSTHOG_CONFIG);

  /** Instancia de posthog una vez cargada e inicializada; null hasta entonces. */
  private _ph: PostHog | null = null;

  /** Resuelve cuando terminó el intento de init (útil para los tests). */
  readonly whenReady: Promise<void>;

  constructor() {
    this.whenReady = this._initPostHog();
  }

  private async _initPostHog(): Promise<void> {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    const { key, host } = this._config;
    if (!key || key === POSTHOG_KEY_PLACEHOLDER) {
      // Sin key real configurada: no inicializamos (dev local / builds sin setear).
      return;
    }

    const posthog = (await import('posthog-js')).default;
    this._ngZone.runOutsideAngular(() => {
      posthog.init(key, {
        api_host: host,
        defaults: '2026-05-30',
      });
    });
    this._ph = posthog;
  }

  /**
   * Captura un evento de producto. Tipado contra el catálogo: el nombre y las
   * props tienen que existir en `PosthogEventMap`.
   */
  captureEvent<K extends PosthogEventName>(name: K, properties: PosthogEventMap[K]): void {
    if (!this._ph) {
      return;
    }
    this._ngZone.runOutsideAngular(() => this._ph!.capture(name, properties));
  }

  /** Captura manual de una excepción (además de la autocaptura del ErrorHandler). */
  captureException(error: unknown, properties?: Record<string, unknown>): void {
    if (!this._ph) {
      return;
    }
    this._ngZone.runOutsideAngular(() => this._ph!.captureException(error, properties));
  }

  /** Asocia los eventos siguientes a un usuario. `properties` no debe incluir PII. */
  identify(distinctId: string, properties?: Record<string, unknown>): void {
    if (!this._ph) {
      return;
    }
    this._ngZone.runOutsideAngular(() => this._ph!.identify(distinctId, properties));
  }

  /** Corta la asociación con el usuario actual (usar en el logout). */
  reset(): void {
    if (!this._ph) {
      return;
    }
    this._ngZone.runOutsideAngular(() => this._ph!.reset());
  }
}
